import {io} from 'socket.io-client';
import msgpackParser from 'socket.io-msgpack-parser';
import {decodeStrokes, encodeStrokes} from './utils/strokes';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const socket = io(API_URL, { parser: msgpackParser });

// Force reconnect when the browser tab becomes visible again.
document.addEventListener('visibilitychange', () => {
    if (!document.hidden && !socket.connected) {
        socket.connect();
    }
});

// Events registered per-room that must be cleaned up on leave.
const ROOM_EVENTS = [
    'chat message', 'user connection', 'user history',
    'strokes', 'strokes ack', 'clear', 'mouse', 'tool settings',
    'add layers', 'remove layer', 'move layers',
    'room private', 'undo replay', 'undo delta', 'room info', 'room owner', 'room full',
    'guess round start', 'guess round end', 'guess word', 'guess correct',
    'guess user list', 'guess vote ack', 'guess score', 'guess period end', 'chat rate limited', 'name taken',
    'kicked', 'ai blocked', 'reset layers', 'publish drawing ack',
];

export const GAME_TYPES = [
    { key: 'draw',    labelKey: 'gameMode.draw',    icon: '✏️', descKey: 'gameMode.drawDesc' },
    { key: 'guess',   labelKey: 'gameMode.guess',   icon: '❓', descKey: 'gameMode.guessDesc' },
    { key: 'compete', labelKey: 'gameMode.compete', icon: '🏆', descKey: 'gameMode.competeDesc' },
];

export function listRooms(onRoomList) {
    socket.once('room list', onRoomList);
    socket.emit('list rooms');
}

export function subscribeToChat(onMessage, onConnection, onUserHistory, name, room, gameType, drawTime, minPlayers, everyoneDraws, wordSet, isPrivate, maxUsers, totalRounds, lang, botKey, spectator, customWords, customWordsName) {
    socket.on('chat message', msg => onMessage(null, msg));
    socket.on('user connection', msg => onConnection(null, msg));
    socket.on('user history', msg => onUserHistory(null, msg));
    socket.emit('subscribeToChat', {name, room, gameType, drawTime, minPlayers, everyoneDraws, wordSet, isPrivate, maxUsers, totalRounds, lang, botKey: botKey || undefined, spectator: spectator || undefined, customWords: customWords || undefined, customWordsName: customWordsName || undefined});
}

export function subscribeToCanvas(onStrokes, onClear, onMouse, onToolSettings, onAddLayers, onRemoveLayer, onMoveLayer, onStrokesAck, onResetLayers) {
    socket.on('strokes', msg => {
        // Binary path: { l, data } where data is an ArrayBuffer/Uint8Array
        if (msg && msg.data) {
            const decoded = decodeStrokes(msg.data);
            const withLayer = decoded.map(s => ({ l: msg.l, ...s }));
            onStrokes(null, withLayer);
        } else if (Array.isArray(msg)) {
            // Legacy JSON path (fallback)
            onStrokes(null, msg);
        }
    });
    // Ack for sender's own strokes — add to history only, already rendered locally
    socket.on('strokes ack', msg => {
        if (msg && msg.data && onStrokesAck) {
            const decoded = decodeStrokes(msg.data);
            const withLayer = decoded.map(s => ({ l: msg.l, ...s }));
            onStrokesAck(null, withLayer);
        }
    });
    socket.on('clear', msg => onClear(null, msg));
    socket.on('mouse', msg => onMouse(null, msg));
    socket.on('tool settings', msg => onToolSettings(null, msg));
    socket.on('add layers', msg => onAddLayers(null, msg));
    socket.on('remove layer', msg => onRemoveLayer(null, msg));
    socket.on('move layers', msg => onMoveLayer(null, msg));
    if (onResetLayers) socket.on('reset layers', msg => onResetLayers(null, msg));
    socket.emit('subscribeToCanvas');
}

export function onRoomPrivateChange(callback) {
    socket.on('room private', callback);
}

export function onRoomInfo(callback) {
    socket.on('room info', callback);
}

export function onRoomFull(callback) {
    socket.on('room full', callback);
}

export function onNameTaken(callback) {
    socket.on('name taken', callback);
}

export function onRoomOwner(callback) {
    socket.on('room owner', callback);
}

export function onUndoReplay(callback) {
    socket.on('undo replay', callback);
}

export function onUndoDelta(callback) {
    socket.on('undo delta', callback);
}

export function sendChatMessage(msg) {
    socket.emit('chat message', msg);
}

export function sendStrokes(msg) {
    // Encode strokes as binary before sending
    if (msg && msg.d && msg.d.length > 0) {
        const data = encodeStrokes(msg.d);
        socket.emit('strokes', { l: msg.l, data });
    }
}

export function sendMouse(msg) {
    socket.emit('mouse', msg);
}

export function sendSettings(msg) {
    socket.emit('tool settings', msg);
}

export function sendClear(msg) {
    socket.emit('clear', msg);
}

export function sendMoveLayers(msg) {
    socket.emit('move layers', msg);
}

let _pendingLayerAdd = false;
export function getPendingLayerAdd() { return _pendingLayerAdd; }
export function clearPendingLayerAdd() { _pendingLayerAdd = false; }

export function sendAddLayer() {
    _pendingLayerAdd = true;
    socket.emit('add layers');
}

export function sendRemoveLayer(msg) {
    socket.emit('remove layer', msg);
}

export function sendUndoPoint() {
    socket.emit('undo point');
}

export function sendUndo() {
    socket.emit('undo');
}

export function setRoomPrivate(isPrivate) {
    socket.emit('set private', isPrivate);
}

export function setRoomAiAllowed(allowed) {
    socket.emit('set ai allowed', { allowed: !!allowed });
}

export function sendThumbnail(data) {
    socket.emit('room thumbnail', data);
}

export function leaveRoom() {
    // Remove all per-room listeners so they don't stack on the next join.
    ROOM_EVENTS.forEach(evt => socket.off(evt));
    socket.emit('leave room');
}

// ── Guess-mode events ───────────────────────────────────────────────────────

export function sendGuessOptIn(wantIn) {
    socket.emit('guess opt-in', wantIn);
}

export function sendAfkToggle(wantAfk) {
    socket.emit('afk toggle', !!wantAfk);
}

export function onGuessRoundStart(callback) {
    socket.on('guess round start', callback);
}

export function onGuessRoundEnd(callback) {
    socket.on('guess round end', callback);
}

export function onGuessWord(callback) {
    socket.on('guess word', callback);
}

export function onGuessHint(callback) {
    socket.on('guess hint', callback);
}

export function onGuessCorrect(callback) {
    socket.on('guess correct', callback);
}

export function onGuessUserList(callback) {
    socket.on('guess user list', callback);
}

export function sendGuessVote(rating) {
    socket.emit('guess vote', rating);
}

export function sendGuessSkip() {
    socket.emit('guess skip');
}

export function sendWordChoice(index) {
    socket.emit('guess word choice', index);
}

export function onGuessVoteAck(callback) {
    socket.on('guess vote ack', callback);
}

export function onGuessScore(callback) {
    socket.on('guess score', callback);
}

export function onGuessPeriodEnd(callback) {
    socket.on('guess period end', callback);
}

export function onChatRateLimited(callback) {
    socket.on('chat rate limited', callback);
}

export function sendVoteKick(targetName) {
    socket.emit('vote kick', { target: targetName });
}

export function sendTransferOwnership(targetName) {
    socket.emit('transfer ownership', { target: targetName });
}

export function onKicked(callback) {
    socket.on('kicked', callback);
}

export function onAiBlocked(callback) {
    socket.on('ai blocked', callback);
}

export function publishDrawing(payload) {
    socket.emit('publish drawing', payload || {});
}

export function onPublishDrawingAck(callback) {
    socket.on('publish drawing ack', callback);
}

// ── Connection health ───────────────────────────────────────────────────────
// status: 'connected' | 'disconnected' | 'reconnecting'
export function onSocketStatus(callback) {
    socket.on('connect',           () => callback('connected'));
    socket.on('disconnect',        () => callback('disconnected'));
    socket.on('connect_error',     () => callback('disconnected'));
    socket.on('reconnect_attempt', () => callback('reconnecting'));
    socket.on('reconnect',         () => callback('connected'));
}

// Detect connection loss while user is in a room.
// Fires callback on reconnect (room state lost) or after timeout (server unreachable).
// Returns a cleanup function.
const CONNECTION_LOST_TIMEOUT = 15000;
export function onConnectionLostFromRoom(callback) {
    let timer = null;
    let active = false;

    const cleanup = () => {
        if (timer) { clearTimeout(timer); timer = null; }
        socket.off('disconnect', onDisconnect);
        socket.off('connect', onReconnect);
        active = false;
    };

    const onReconnect = () => {
        if (!active) return;
        cleanup();
        callback();
    };

    const onDisconnect = (reason) => {
        if (reason === 'io client disconnect') return; // intentional leave
        active = true;
        socket.once('connect', onReconnect);
        timer = setTimeout(() => {
            cleanup();
            callback();
        }, CONNECTION_LOST_TIMEOUT);
    };

    socket.on('disconnect', onDisconnect);
    return cleanup;
}
